import { getSimpleBoolSelectItems, ifAll, IFieldInfoProperties, not, TGetValueFn } from "@components/smart/FieldInfo";
import { IFormGroupDef } from "@components/smart/smartFormGroup/SmartFormGroup";
import { createPath, IEntity } from "@odata/BindingContext";
import {
    CollectiveAgreementTypeEntity,
    CompanyEntity,
    CountryEntity,
    CsszEmploymentTypeEntity,
    EntitySetName,
    EntityTypeName,
    IPrJmhzEmployeeEntity,
    OwnershipFormTypeEntity,
    PrChildDeductionOrderEntity,
    PrEldpItemTypeEntity,
    PrEmploymentEntity,
    PrJmhzAnnualOverviewEntity,
    PrJmhzAnnualTaxSettlementPartnerEntity,
    PrJmhzAnnualTaxSettlementSupportedChildEntity,
    PrJmhzAnnualTaxSettlementSupportingPersonEntity,
    PrJmhzCollectiveAgreementEntity,
    PrJmhzCompanyEntity,
    PrJmhzDetailEntity,
    PrJmhzEldpDaysDeductedEntity,
    PrJmhzEldpDaysExcludedEntity,
    PrJmhzEldpItemEntity,
    PrJmhzEmployeeAnnualPaymentOverviewEntity,
    PrJmhzEmployeeAnnualTaxSettlementEntity,
    PrJmhzEmployeeEntity,
    PrJmhzEmployeeIncomeOverviewEntity,
    PrJmhzEmployeeIncomeTaxOverviewEntity,
    PrJmhzEmployeeSalaryOverviewEntity,
    PrJmhzEmployeeWithholdingTaxOverviewEntity,
    PrJmhzEmploymentAttendanceOverviewEntity,
    PrJmhzEmploymentEntity,
    PrJmhzEmploymentIncomeOverviewEntity,
    PrJmhzEmploymentSocialSecurityOverviewEntity,
    PrJmhzFormTypeEntity,
    PrJmhzIncomeTaxOverviewEntity,
    PrJmhzSocialInsuranceOverviewEntity,
    PrJmhzSubmissionEntity,
    PrJmhzSupportedChildEntity,
    PrJmhzSupportingPersonEntity,
    PrSocialInsuranceDiscountTypeEntity,
    PrSubmissionSubtypeEntity,
    RiskCategoryEntity,
    SpecificLegalFactTypeEntity,
    YesNoEntity
} from "@odata/GeneratedEntityTypes";
import { PrSubmissionTypeCode, YesNoCode } from "@odata/GeneratedEnums";
import { setDefByEntityType } from "@pages/getDefByEntityType";
import { IDefinition, IGetDefinition } from "@pages/PageUtils";
import { createDateFromPeriod, getPayrollCurrencyFieldDef } from "@pages/payroll/payroll.utils";
import {
    getDefinitions as commonDef,
    getSubmissionFormDef,
    IGetSubmissionDefArgs,
    PrSubmissionGroup
} from "@pages/payroll/submission/Submission.def";
import {
    getAddressAutocompleteDef,
    getJmhzIdDef,
    ifTruthy,
    isInMonths,
    isNonZeroValue,
    oneOfIsDefined
} from "@pages/payroll/submission/submissionTypes/PrJmhzSubmission.utils";
import PrJmhzSubmissionFormView from "@pages/payroll/submission/submissionTypes/PrJmhzSubmissionFormView";
import { isDefined } from "@utils/general";
import { capitalize } from "@utils/string";
import i18next from "i18next";
import { cloneDeep } from "lodash";

import { IAppContext } from "../../../../contexts/appContext/AppContext.types";
import { FieldType } from "../../../../enums";
import { DateFormat, getUtcDayjs } from "../../../../types/Date";
import { addFormDef, addFormGroup, FormType, getFormGroup } from "../../../../views/formView/Form.utils";

export const _detailPath = (path: PrJmhzDetailEntity, pref = true): string =>
    pref ? createPath(PrJmhzSubmissionEntity.Detail, path) : path;
export const _annualOverviewPath = (path: PrJmhzAnnualOverviewEntity): string =>
    createPath(PrJmhzSubmissionEntity.Detail, PrJmhzDetailEntity.AnnualOverview, path);

export const _employeePath = (path?: PrJmhzEmployeeEntity, pref = true): string =>
    pref ? createPath(_detailPath(PrJmhzDetailEntity.Employees), path) : path;
export const _employeeIncomeOverviewPath = (path: PrJmhzEmployeeIncomeOverviewEntity, pref = true): string =>
    createPath((pref ? _employeePath(PrJmhzEmployeeEntity.IncomeOverview) : PrJmhzEmployeeEntity.IncomeOverview), path);
export const _employeeSalaryOverviewPath = (path: PrJmhzEmployeeSalaryOverviewEntity, pref = true): string =>
    createPath((pref ? _employeePath(PrJmhzEmployeeEntity.SalaryOverview) : PrJmhzEmployeeEntity.SalaryOverview), path);

export const _employmentPath = (path?: PrJmhzEmploymentEntity, pref = true): string =>
    pref ? createPath(PrJmhzSubmissionEntity.Detail, PrJmhzDetailEntity.Employees, PrJmhzEmployeeEntity.Employments, path)
        : path;
export const _employmentIncomeOverviewPath = (path: PrJmhzEmploymentIncomeOverviewEntity, pref = true): string =>
    createPath(_employmentPath(PrJmhzEmploymentEntity.IncomeOverview, pref), path);


const getYesNoBooleanFieldDef: () => IFieldInfoProperties = () => ({
    type: FieldType.ComboBox,
    fieldSettings: {
        items: getSimpleBoolSelectItems()
    }
});

function ifEmploymentHas(pathFromEmployment: string): TGetValueFn<boolean> {
    return (args) => {
        const { storage } = args;
        const employeesBc = storage.data.bindingContext
            .navigate(PrJmhzSubmissionEntity.Detail)
            .navigate(PrJmhzDetailEntity.Employees);
        const employees = storage.getValue(employeesBc);
        return employees.some((employee: IPrJmhzEmployeeEntity) => {
            const employmentsBc = employeesBc.addKey(employee).navigate(PrJmhzEmployeeEntity.Employments);
            return employee.Employments?.some(employment => {
                const propBc = employmentsBc.addKey(employment).navigate(pathFromEmployment);
                const val = storage.getValue(propBc, { useDirectValue: false });
                return isDefined(val) && val !== false && val !== YesNoCode.No;
            });
        });
    };
}

const hasSocialInsuranceDiscount: TGetValueFn<boolean> = (args) => {
    const { storage, bindingContext } = args;
    const socialInsurance = storage.getValue(bindingContext.getParent());
    return [
        PrJmhzSocialInsuranceOverviewEntity.EmployerDiscountEmployeeCount,
        PrJmhzSocialInsuranceOverviewEntity.EmployerDiscountCalculationBase,
        PrJmhzSocialInsuranceOverviewEntity.EmployerDiscountAmount,
        PrJmhzSocialInsuranceOverviewEntity.EmployeeDiscountEmployeeCount,
        PrJmhzSocialInsuranceOverviewEntity.EmployeeDiscountCalculationBase,
        PrJmhzSocialInsuranceOverviewEntity.EmployeeDiscountAmount,
        PrJmhzSocialInsuranceOverviewEntity.EmployeeOvoZelDiscountEmployeeCount,
        PrJmhzSocialInsuranceOverviewEntity.EmployeeOvoZelDiscountCalculationBase,
        PrJmhzSocialInsuranceOverviewEntity.EmployeeOvoZelDiscountAmount,
    ].some(key =>
        isDefined(socialInsurance[key]) && socialInsurance[key] !== 0);
};

export const getDefinitions: IGetDefinition = (context: IAppContext, args?: IGetSubmissionDefArgs): IDefinition => {
    const d = cloneDeep(getSubmissionFormDef(PrJmhzSubmissionFormView, context, {
        ...args,
        type: PrSubmissionTypeCode.MonthlyReport
    }));

    d.form.type = FormType.VirtualizedList;
    d.form.additionalProperties = [
        {
            id: createPath(PrJmhzSubmissionEntity.Detail, PrJmhzDetailEntity.Employees, PrJmhzEmployeeEntity.Employments, PrJmhzEmploymentEntity.Employment),
            additionalProperties: [
                { id: PrEmploymentEntity.Id },
                { id: PrEmploymentEntity.Name }
            ]
        },
        { id: createPath(PrJmhzSubmissionEntity.Detail, PrJmhzDetailEntity.Employees, PrJmhzEmployeeEntity.Employee, PrEmploymentEntity.Id) },
        { id: createPath(PrJmhzSubmissionEntity.Detail, PrJmhzDetailEntity.Company, PrJmhzCompanyEntity.Company, CompanyEntity.Id) }
    ];

    const mainGroup = getFormGroup(d, PrSubmissionGroup.Main);
    addFormDef(d, PrJmhzSubmissionEntity.SubmissionGuid, {
        ...getJmhzIdDef(1)
    });
    addFormDef(d, PrJmhzSubmissionEntity.DateSubmission, {
        type: FieldType.DateTime,
        isRequired: true,
        fieldSettings: {
            maxDate: () => getUtcDayjs().toDate()
        },
        ...getJmhzIdDef(5)
    });
    addFormDef(d, PrJmhzSubmissionEntity.SubmissionSubtype, {
        type: FieldType.ComboBox,
        isRequired: true,
        isDisabled: true,
        fieldSettings: {
            displayName: PrSubmissionSubtypeEntity.Name
        },
        ...getJmhzIdDef(7)
    });
    addFormDef(d, _detailPath(PrJmhzDetailEntity.Month), {
        formatter: (val, opts) => {
            const period = opts.storage.getValueByPath(PrJmhzSubmissionEntity.Detail);
            const date = createDateFromPeriod(period);
            return capitalize(getUtcDayjs(date).format(DateFormat.month));
        },
        isDisabled: true,
        ...getJmhzIdDef(10)
    });
    addFormDef(d, _detailPath(PrJmhzDetailEntity.Year), {
        isDisabled: true,
        formatter: (val) => val.toString(), // no formatting, it's just year as number
        ...getJmhzIdDef(11)
    });
    addFormDef(d, _detailPath(PrJmhzDetailEntity.NotificationEmail), {});

    mainGroup.title = i18next.t("Submission:JmhzForm.Metadata");
    mainGroup.rows = [[
        { id: PrJmhzSubmissionEntity.SubmissionGuid },
        { id: PrJmhzSubmissionEntity.DateSubmission },
        { id: PrJmhzSubmissionEntity.SubmissionSubtype },
        { id: _detailPath(PrJmhzDetailEntity.Month) },
        { id: _detailPath(PrJmhzDetailEntity.Year) },
        { id: _detailPath(PrJmhzDetailEntity.NotificationEmail) },
    ]];


    // *********************** Insurance amount overview group ***********************
    const _socialInsurancePath = (path: PrJmhzSocialInsuranceOverviewEntity) =>
        createPath(PrJmhzSubmissionEntity.Detail, PrJmhzDetailEntity.SocialInsuranceOverview, path);

    const calculationBaseAttrs: IFieldInfoProperties = {
        isRequired: not(oneOfIsDefined((bc) => [
            bc.navigateWithSiblingFallback(PrJmhzSocialInsuranceOverviewEntity.RegularCalculationBase),
            bc.navigateWithSiblingFallback(PrJmhzSocialInsuranceOverviewEntity.RescueCalculationBase),
            bc.navigateWithSiblingFallback(PrJmhzSocialInsuranceOverviewEntity.RiskInsuranceByEmployer),
        ])),
        affectedFields: [
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.RegularCalculationBase), revalidate: true },
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.RescueCalculationBase), revalidate: true },
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.RiskInsuranceByEmployer), revalidate: true },
        ]
    };

    addFormDef(d, _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.RegularCalculationBase), {
        ...calculationBaseAttrs,
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(23),
        affectedFields: [
            ...calculationBaseAttrs.affectedFields,
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.RegularInsuranceByEmployer) }
        ]
    });

    addFormDef(d, _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.RegularInsuranceByEmployer), {
        isRequired: true,
        isVisible: isNonZeroValue((bc) => bc.getParent().navigate(PrJmhzSocialInsuranceOverviewEntity.RegularCalculationBase)),
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(24)
    });

    addFormDef(d, _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.RescueCalculationBase), {
        ...calculationBaseAttrs,
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(25)
    });
    addFormDef(d, _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.RescueInsuranceByEmployer), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(26)
    });
    addFormDef(d, _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.RiskCalculationBase), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(483)
    });
    addFormDef(d, _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.RiskInsuranceByEmployer), {
        ...calculationBaseAttrs,
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(484)
    });

    addFormDef(d, _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.InsuranceByEmployerTotal), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(27)
    });
    addFormDef(d, _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.InsuranceByEmployeeTotal), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(28)
    });
    addFormDef(d, _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.SocialInsuranceTotal), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(29)
    });

    addFormDef(d, _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.EmployerDiscountEmployeeCount), {
        isRequired: true,
        isVisible: ifEmploymentHas(createPath(PrJmhzEmploymentEntity.SocialSecurityOverview, PrJmhzEmploymentSocialSecurityOverviewEntity.HasEmployerInsuranceDiscount)),
        ...getJmhzIdDef(30),
        affectedFields: [
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.SocialInsuranceToPay), revalidate: true }
        ]
    });
    addFormDef(d, _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.EmployerDiscountCalculationBase), {
        isRequired: true,
        isVisible: ifEmploymentHas(createPath(PrJmhzEmploymentEntity.SocialSecurityOverview, PrJmhzEmploymentSocialSecurityOverviewEntity.HasEmployerInsuranceDiscount)),
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(31),
        affectedFields: [
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.SocialInsuranceToPay), revalidate: true }
        ]
    });
    addFormDef(d, _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.EmployerDiscountAmount), {
        isRequired: true,
        isVisible: ifEmploymentHas(createPath(PrJmhzEmploymentEntity.SocialSecurityOverview, PrJmhzEmploymentSocialSecurityOverviewEntity.HasEmployerInsuranceDiscount)),
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(32),
        affectedFields: [
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.SocialInsuranceToPay), revalidate: true }
        ]
    });

    addFormDef(d, _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.EmployeeDiscountEmployeeCount), {
        isRequired: true,
        isVisible: ifEmploymentHas(createPath(PrJmhzEmploymentEntity.SocialSecurityOverview, PrJmhzEmploymentSocialSecurityOverviewEntity.HasRetireeDiscount)),
        ...getJmhzIdDef(485),
        affectedFields: [
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.SocialInsuranceToPay), revalidate: true }
        ]
    });
    addFormDef(d, _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.EmployeeDiscountCalculationBase), {
        isRequired: true,
        isVisible: ifEmploymentHas(createPath(PrJmhzEmploymentEntity.SocialSecurityOverview, PrJmhzEmploymentSocialSecurityOverviewEntity.HasRetireeDiscount)),
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(486),
        affectedFields: [
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.SocialInsuranceToPay), revalidate: true }
        ]
    });
    addFormDef(d, _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.EmployeeDiscountAmount), {
        isRequired: true,
        isVisible: ifEmploymentHas(createPath(PrJmhzEmploymentEntity.SocialSecurityOverview, PrJmhzEmploymentSocialSecurityOverviewEntity.HasRetireeDiscount)),
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(487),
        affectedFields: [
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.SocialInsuranceToPay), revalidate: true }
        ]
    });

    addFormDef(d, _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.EmployeeOvoZelDiscountEmployeeCount), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(543),
        affectedFields: [
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.SocialInsuranceToPay), revalidate: true }
        ]
    });
    addFormDef(d, _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.EmployeeOvoZelDiscountCalculationBase), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(544),
        affectedFields: [
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.SocialInsuranceToPay), revalidate: true }
        ]
    });
    addFormDef(d, _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.EmployeeOvoZelDiscountAmount), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(545),
        affectedFields: [
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.SocialInsuranceToPay), revalidate: true }
        ]
    });
    addFormDef(d, _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.SocialInsuranceToPay), {
        isRequired: true,
        isVisible: hasSocialInsuranceDiscount,
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(33)
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.InsuranceAmountOverview,
        title: i18next.t("Submission:JmhzForm.InsuranceAmountOverview"),
        rows: [[
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.RegularCalculationBase) },
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.RegularInsuranceByEmployer) },
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.RescueCalculationBase) },
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.RescueInsuranceByEmployer) },
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.RiskCalculationBase) },
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.RiskInsuranceByEmployer) },
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.InsuranceByEmployerTotal) },
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.InsuranceByEmployeeTotal) },
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.SocialInsuranceTotal) },
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.EmployerDiscountEmployeeCount) },
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.EmployerDiscountCalculationBase) },
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.EmployerDiscountAmount) },
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.EmployeeDiscountEmployeeCount) },
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.EmployeeDiscountCalculationBase) },
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.EmployeeDiscountAmount) },
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.EmployeeOvoZelDiscountEmployeeCount) },
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.EmployeeOvoZelDiscountCalculationBase) },
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.EmployeeOvoZelDiscountAmount) },
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.SocialInsuranceToPay) },
        ]]
    });


    // *********************** Income tax overview group ***********************
    const _incomeTaxPath = (path: PrJmhzIncomeTaxOverviewEntity) =>
        createPath(PrJmhzSubmissionEntity.Detail, PrJmhzDetailEntity.IncomeTaxOverview, path);

    addFormDef(d, _incomeTaxPath(PrJmhzIncomeTaxOverviewEntity.AdvancedTaxAfterDiscount), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(34)
    });
    addFormDef(d, _incomeTaxPath(PrJmhzIncomeTaxOverviewEntity.MonthlyTaxBonus), {
        isRequired: true,
        isVisible: isNonZeroValue(bc => bc.getParent().navigate(PrJmhzIncomeTaxOverviewEntity.AdvancedTaxAfterDiscount)),
        defaultValue: 0,
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(35)
    });
    addFormDef(d, _incomeTaxPath(PrJmhzIncomeTaxOverviewEntity.AnnualTaxSettlementTaxOverpayment), {
        isRequired: true, // todo: povinné, ak bolo v danom mesiaci urobené ročné zúčtovanie
        isVisible: isInMonths([1, 2, 3]),
        defaultValue: 0,
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(36)
    });
    addFormDef(d, _incomeTaxPath(PrJmhzIncomeTaxOverviewEntity.AnnualTaxSettlementTaxBonusPayment), {
        isRequired: true,
        isVisible: ifAll(isInMonths([1, 2, 3]), isNonZeroValue(bc => bc.getParent().navigate(PrJmhzIncomeTaxOverviewEntity.AdvancedTaxAfterDiscount))),
        defaultValue: 0,
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(37)
    });

    addFormDef(d, _annualOverviewPath(PrJmhzAnnualOverviewEntity.DisabledEmployeeShare), {
        isVisible: isInMonths([12]),
        defaultValue: 0,
        ...getJmhzIdDef(452)
    });
    addFormDef(d, _annualOverviewPath(PrJmhzAnnualOverviewEntity.AverageAnnualEmployeeCount), {
        isVisible: isInMonths([12]),
        defaultValue: 0,
        ...getJmhzIdDef(38)
    });
    addFormDef(d, _annualOverviewPath(PrJmhzAnnualOverviewEntity.AverageAnnualDisabledEmployeeCount), {
        isVisible: isInMonths([12]),
        defaultValue: 0,
        ...getJmhzIdDef(39)
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.IncomeTaxOverview,
        title: i18next.t("Submission:JmhzForm.IncomeTaxOverview"),
        rows: [[
            { id: _incomeTaxPath(PrJmhzIncomeTaxOverviewEntity.AdvancedTaxAfterDiscount) },
            { id: _incomeTaxPath(PrJmhzIncomeTaxOverviewEntity.MonthlyTaxBonus) },
            { id: _incomeTaxPath(PrJmhzIncomeTaxOverviewEntity.AnnualTaxSettlementTaxOverpayment) },
            { id: _incomeTaxPath(PrJmhzIncomeTaxOverviewEntity.AnnualTaxSettlementTaxBonusPayment) },
            { id: _annualOverviewPath(PrJmhzAnnualOverviewEntity.DisabledEmployeeShare) },
            { id: _annualOverviewPath(PrJmhzAnnualOverviewEntity.AverageAnnualEmployeeCount) },
            { id: _annualOverviewPath(PrJmhzAnnualOverviewEntity.AverageAnnualDisabledEmployeeCount) },
        ]]
    });


    // *********************** Employer group ***********************
    addFormDef(d, _detailPath(PrJmhzDetailEntity.SpecificLegalFactType), {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: SpecificLegalFactTypeEntity.Name
        },
        ...getJmhzIdDef(408),
        affectedFields: [
            { id: _detailPath(PrJmhzDetailEntity.DateSpecificLegalFact) }
        ]
    });
    addFormDef(d, _detailPath(PrJmhzDetailEntity.DateSpecificLegalFact), {
        isRequired: true,
        isVisible: (args) => {
            const { storage, bindingContext } = args;
            const specificLegalFactTypeBc = bindingContext.navigateWithSiblingFallback(PrJmhzDetailEntity.SpecificLegalFactType);
            const specificLegalFactTypeCode = storage.getValue(specificLegalFactTypeBc, { useDirectValue: false });
            return isDefined(specificLegalFactTypeCode);
        },
        ...getJmhzIdDef(409)
    });

    addFormDef(d, createPath(
        _annualOverviewPath(PrJmhzAnnualOverviewEntity.CollectiveAgreementTypes),
        PrJmhzCollectiveAgreementEntity.CollectiveAgreementType
    ), {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: CollectiveAgreementTypeEntity.MpsvCode,
            shouldDisplayAdditionalColumns: true,
            orderBy: {
                propertyName: CollectiveAgreementTypeEntity.MpsvCode,
                asc: true
            },
            preloadItems: true
        },
        columns: [
            { id: CollectiveAgreementTypeEntity.MpsvCode },
            { id: CollectiveAgreementTypeEntity.Name },
        ],
        ...getJmhzIdDef(214)
    });

    addFormDef(d, _annualOverviewPath(PrJmhzAnnualOverviewEntity.OwnershipFormType), {
        type: FieldType.ComboBox,
        isRequired: true,
        isVisible: isInMonths([12]),
        fieldSettings: {
            displayName: OwnershipFormTypeEntity.MpsvCode,
            shouldDisplayAdditionalColumns: true,
            orderBy: {
                propertyName: OwnershipFormTypeEntity.MpsvCode,
                asc: true
            },
            preloadItems: true
        },
        columns: [
            { id: OwnershipFormTypeEntity.MpsvCode },
            { id: OwnershipFormTypeEntity.Name },
        ],
        ...getJmhzIdDef(220)
    });
    const symbolVariablePath = createPath(_detailPath(PrJmhzDetailEntity.Company), PrJmhzCompanyEntity.CSSZSymbolVariable);
    addFormDef(d, symbolVariablePath, {
        ...getJmhzIdDef(221)
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.Employer,
        title: i18next.t("Submission:JmhzForm.Employer"),
        rows: [[
            { id: _detailPath(PrJmhzDetailEntity.SpecificLegalFactType) },
            { id: _detailPath(PrJmhzDetailEntity.DateSpecificLegalFact) },
            { id: _annualOverviewPath(PrJmhzAnnualOverviewEntity.OwnershipFormType) },
            { id: symbolVariablePath },
        ]],
        lineItems: {
            collection: _annualOverviewPath(PrJmhzAnnualOverviewEntity.CollectiveAgreementTypes),
            columns: [
                { id: PrJmhzCollectiveAgreementEntity.CollectiveAgreementType }
            ],
            canAdd: isInMonths([12]),
            isTheOnlyItemRemovable: false,
            order: PrJmhzAnnualOverviewEntity.Id
        }
    });

    /**
     * *********************** Employee groups ***********************
     */
    const employeeGroupDef: Partial<IFormGroupDef> = {
        collection: _employeePath(),
        collectionOrder: PrJmhzEmployeeEntity.Id,
    };

    // *********************** Basic info ***********************
    addFormDef(d, _employeePath(PrJmhzEmployeeEntity.MpsvIdentifier), {
        isRequired: true,
        ...getJmhzIdDef(51)
    });
    addFormDef(d, _employeePath(PrJmhzEmployeeEntity.LastName), { isRequired: true, ...getJmhzIdDef(53) });
    addFormDef(d, _employeePath(PrJmhzEmployeeEntity.FirstName), { isRequired: true, ...getJmhzIdDef(54) });
    addFormDef(d, _employeePath(PrJmhzEmployeeEntity.DateOfBirth), { ...getJmhzIdDef(56) });

    addFormGroup(d, {
        id: PrSubmissionGroup.Employee,
        ...employeeGroupDef,
        title: i18next.t("Submission:JmhzForm.EmployeeIdentification"),
        collectionItemTitle: (index: number, item: IEntity) => {
            return [item.FirstName, item.LastName].filter(Boolean).join(" ")
                || i18next.t("Submission:JmhzForm.NthEmployee", { idx: index + 1 });
        },
        showGroupDivider: false,
        rows: [[
            { id: PrJmhzEmployeeEntity.MpsvIdentifier },
            { id: PrJmhzEmployeeEntity.LastName },
            { id: PrJmhzEmployeeEntity.FirstName },
            { id: PrJmhzEmployeeEntity.DateOfBirth },
        ]]
    });

    // *********************** Income ***********************
    addFormDef(d, _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.IncomeTotal), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(286)
    });
    addFormDef(d, _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.SettledIncomeLegalEntityBodyMemberNonResidentRewards), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(416)
    });
    addFormDef(d, _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.NonTaxableIncomeTotal), {
        isRequired: true,
        isVisible: oneOfIsDefined((bc) => [
            bc.navigateWithSiblingFallback(PrJmhzEmployeeIncomeOverviewEntity.EmployerRetirementSavingsContributionTotal),
            bc.navigateWithSiblingFallback(PrJmhzEmployeeIncomeOverviewEntity.LongTermCareInsuranceContribution),
        ]),
        isDisabled: true, // auto calculated
        defaultValue: 0,
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(289)
    });

    addFormDef(d, _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.EmployerRetirementSavingsContributionTotal), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(417),
        affectedFields: [
            { id: _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.NonTaxableIncomeTotal) }
        ]
    });
    addFormDef(d, _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.StateContributedSupplementaryPensionInsuranceContribution), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(292)
    });
    addFormDef(d, _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.SupplementaryPensionSavingsContribution), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(293)
    });
    addFormDef(d, _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.PensionInsuranceContribution), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(294)
    });
    addFormDef(d, _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.PrivateLifeInsuranceContribution), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(295)
    });
    addFormDef(d, _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.LongTermInvestmentProductContribution), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(296)
    });
    addFormDef(d, _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.LongTermCareInsuranceContribution), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(418),
        affectedFields: [
            { id: _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.NonTaxableIncomeTotal) }
        ]
    });

    addFormDef(d, _employeeSalaryOverviewPath(PrJmhzEmployeeSalaryOverviewEntity.SalaryNet), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(344)
    });
    addFormDef(d, _employeeSalaryOverviewPath(PrJmhzEmployeeSalaryOverviewEntity.HasSalaryDeduction), {
        ...getYesNoBooleanFieldDef(),
        ...getJmhzIdDef(116)
    });
    addFormDef(d, _employeeSalaryOverviewPath(PrJmhzEmployeeSalaryOverviewEntity.DatePayment), {
        ...getJmhzIdDef(347)
    });
    addFormDef(d, _employeeSalaryOverviewPath(PrJmhzEmployeeSalaryOverviewEntity.PaidInCash), {
        type: FieldType.ComboBox,
        isRequired: true,
        defaultValue: YesNoCode.No,
        fieldSettings: {
            displayName: YesNoEntity.Name
        },
        ...getJmhzIdDef(348)
    });
    addFormDef(d, _employeeSalaryOverviewPath(PrJmhzEmployeeSalaryOverviewEntity.HasEmployerClaimsWageDeductions), {
        type: FieldType.ComboBox,
        isRequired: true,
        defaultValue: YesNoCode.No,
        fieldSettings: {
            displayName: YesNoEntity.Name
        },
        ...getJmhzIdDef(349)
    });
    addFormDef(d, _employeeSalaryOverviewPath(PrJmhzEmployeeSalaryOverviewEntity.EmployerCivilCodeDeduction), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(350)
    });
    addFormDef(d, _employeeSalaryOverviewPath(PrJmhzEmployeeSalaryOverviewEntity.EmployeeDamageDeduction), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(351)
    });
    addFormDef(d, _employeeSalaryOverviewPath(PrJmhzEmployeeSalaryOverviewEntity.WorkplaceMealsDeduction), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(352)
    });
    addFormDef(d, _employeeSalaryOverviewPath(PrJmhzEmployeeSalaryOverviewEntity.EmployeeLiabilitiesDeduction), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(353)
    });
    addFormDef(d, _employeePath(PrJmhzEmployeeEntity.HealthInsuranceByEmployer), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(482)
    });
    addFormDef(d, _employeePath(PrJmhzEmployeeEntity.HealthInsuranceByEmployee), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(371)
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.IncomeInPeriod,
        ...employeeGroupDef,
        title: i18next.t("Submission:JmhzForm.Income"),
        showGroupDivider: false,
        rows: [[
            // income overview
            { id: _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.IncomeTotal, false) },
            { id: _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.SettledIncomeLegalEntityBodyMemberNonResidentRewards, false) },
            { id: _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.NonTaxableIncomeTotal, false) },
            { id: _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.EmployerRetirementSavingsContributionTotal, false) },
            { id: _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.StateContributedSupplementaryPensionInsuranceContribution, false) },
            { id: _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.SupplementaryPensionSavingsContribution, false) },
            { id: _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.PensionInsuranceContribution, false) },
            { id: _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.PrivateLifeInsuranceContribution, false) },
            { id: _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.LongTermInvestmentProductContribution, false) },
            { id: _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.LongTermCareInsuranceContribution, false) },
            // salary overview
            { id: _employeeSalaryOverviewPath(PrJmhzEmployeeSalaryOverviewEntity.SalaryNet, false) },
            { id: _employeeSalaryOverviewPath(PrJmhzEmployeeSalaryOverviewEntity.HasSalaryDeduction, false) },
            { id: _employeeSalaryOverviewPath(PrJmhzEmployeeSalaryOverviewEntity.DatePayment, false) },
            { id: _employeeSalaryOverviewPath(PrJmhzEmployeeSalaryOverviewEntity.PaidInCash, false) },
            { id: _employeeSalaryOverviewPath(PrJmhzEmployeeSalaryOverviewEntity.HasEmployerClaimsWageDeductions, false) },
            { id: _employeeSalaryOverviewPath(PrJmhzEmployeeSalaryOverviewEntity.EmployerCivilCodeDeduction, false) },
            { id: _employeeSalaryOverviewPath(PrJmhzEmployeeSalaryOverviewEntity.EmployeeDamageDeduction, false) },
            { id: _employeeSalaryOverviewPath(PrJmhzEmployeeSalaryOverviewEntity.WorkplaceMealsDeduction, false) },
            { id: _employeeSalaryOverviewPath(PrJmhzEmployeeSalaryOverviewEntity.EmployeeLiabilitiesDeduction, false) },
            { id: PrJmhzEmployeeEntity.HealthInsuranceByEmployer },
            { id: PrJmhzEmployeeEntity.HealthInsuranceByEmployee },
        ]]
    });

    // *********************** AdvanceTax ***********************
    const _employeeIncomeTaxOverviewPath = (path: PrJmhzEmployeeIncomeTaxOverviewEntity, pref = true) =>
        createPath(pref ? _employeePath(PrJmhzEmployeeEntity.IncomeTaxOverview) : PrJmhzEmployeeEntity.IncomeTaxOverview, path);

    addFormDef(d, _employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.AdvancedTaxBase), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(297)
    });
    addFormDef(d, _employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.AdvancedTaxCalculated), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(298)
    });
    addFormDef(d, _employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.HasTaxpayerDeclaration), {
        ...getYesNoBooleanFieldDef(),
        ...getJmhzIdDef(419)
    });
    addFormDef(d, _employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.TaxPayerDiscount), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(299)
    });
    addFormDef(d, _employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.BasicDisabilityDiscount), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(300)
    });
    addFormDef(d, _employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.ExtendedDisabilityDiscount), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(301)
    });
    addFormDef(d, _employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.ZtpPCardHolderDiscount), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(302)
    });
    addFormDef(d, _employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.MonthlyChildTaxBenefit), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(303)
    });
    addFormDef(d, _employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.MonthlyChildDiscount), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(304)
    });
    addFormDef(d, _employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.AdvancedTaxAfterDiscount), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(305)
    });
    addFormDef(d, _employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.PaidMonthlyTaxBonus), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(306)
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.AdvanceTax,
        ...employeeGroupDef,
        title: i18next.t("Submission:JmhzForm.AdvanceTax"),
        showGroupDivider: false,
        rows: [[
            // income overview
            { id: _employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.AdvancedTaxBase, false) },
            { id: _employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.AdvancedTaxCalculated, false) },
            { id: _employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.HasTaxpayerDeclaration, false) },
            { id: _employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.TaxPayerDiscount, false) },
            { id: _employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.BasicDisabilityDiscount, false) },
            { id: _employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.ExtendedDisabilityDiscount, false) },
            { id: _employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.ZtpPCardHolderDiscount, false) },
            { id: _employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.MonthlyChildTaxBenefit, false) },
            { id: _employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.MonthlyChildDiscount, false) },
            { id: _employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.AdvancedTaxAfterDiscount, false) },
            { id: _employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.PaidMonthlyTaxBonus, false) },
        ]]
    });

    // *********************** SupportedChildren ***********************
    const _employeeSupportedChildrenPath = (path: PrJmhzSupportedChildEntity) =>
        createPath(_employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.SupportedChildren), path);

    addFormDef(d, _employeeSupportedChildrenPath(PrJmhzSupportedChildEntity.FirstName), {
        ...getJmhzIdDef(435)
    });
    addFormDef(d, _employeeSupportedChildrenPath(PrJmhzSupportedChildEntity.LastName), {
        ...getJmhzIdDef(436)
    });
    addFormDef(d, _employeeSupportedChildrenPath(PrJmhzSupportedChildEntity.DateOfBirth), {
        isRequired: not(oneOfIsDefined((bc) => [
            bc.getParent().navigate(PrJmhzSupportedChildEntity.PersonalIdentificationNumber),
        ])),
        affectedFields: [
            { id: PrJmhzSupportedChildEntity.PersonalIdentificationNumber, navigateFromParent: true, revalidate: true }
        ],
        ...getJmhzIdDef(437)
    });
    addFormDef(d, _employeeSupportedChildrenPath(PrJmhzSupportedChildEntity.PersonalIdentificationNumber), {
        isRequired: not(oneOfIsDefined((bc) => [
            bc.getParent().navigate(PrJmhzSupportedChildEntity.DateOfBirth),
        ])),
        affectedFields: [
            { id: PrJmhzSupportedChildEntity.DateOfBirth, navigateFromParent: true, revalidate: true }
        ],
        ...getJmhzIdDef(438)
    });
    addFormDef(d, _employeeSupportedChildrenPath(PrJmhzSupportedChildEntity.IsZtpP), {
        ...getYesNoBooleanFieldDef(),
        ...getJmhzIdDef(439)
    });
    addFormDef(d, _employeeSupportedChildrenPath(PrJmhzSupportedChildEntity.Order), {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: PrChildDeductionOrderEntity.Name
        },
        ...getJmhzIdDef(440)
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.SupportedChildren,
        ...employeeGroupDef,
        title: i18next.t("Submission:JmhzForm.SupportedChildren"),
        showGroupDivider: false,
        lineItems: {
            collection: createPath(PrJmhzEmployeeEntity.IncomeTaxOverview, PrJmhzEmployeeIncomeTaxOverviewEntity.SupportedChildren),
            order: PrJmhzSupportedChildEntity.Id,
            canAdd: true,
            columns: [
                { id: PrJmhzSupportedChildEntity.FirstName },
                { id: PrJmhzSupportedChildEntity.LastName },
                { id: PrJmhzSupportedChildEntity.DateOfBirth },
                { id: PrJmhzSupportedChildEntity.PersonalIdentificationNumber },
                { id: PrJmhzSupportedChildEntity.IsZtpP },
                { id: PrJmhzSupportedChildEntity.Order }
            ]
        }
    });

    // *********************** SupportingPersons ***********************
    const _employeeSupportingPersonsPath = (path: PrJmhzSupportingPersonEntity) =>
        createPath(_employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.SupportingPersons), path);

    addFormDef(d, _employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.HasOtherSupportingPerson), {
        ...getYesNoBooleanFieldDef(),
        isRequired: true,
        defaultValue: false,
        ...getJmhzIdDef(453)
    });
    addFormDef(d, _employeeSupportingPersonsPath(PrJmhzSupportingPersonEntity.FirstName), {
        ...getJmhzIdDef(431)
    });
    addFormDef(d, _employeeSupportingPersonsPath(PrJmhzSupportingPersonEntity.LastName), {
        ...getJmhzIdDef(432)
    });
    addFormDef(d, _employeeSupportingPersonsPath(PrJmhzSupportingPersonEntity.DateOfBirth), {
        isRequired: not(oneOfIsDefined((bc) => [
            bc.getParent().navigate(PrJmhzSupportingPersonEntity.PersonalIdentificationNumber),
        ])),
        affectedFields: [
            { id: PrJmhzSupportingPersonEntity.PersonalIdentificationNumber, navigateFromParent: true, revalidate: true }
        ],
        ...getJmhzIdDef(433)
    });
    addFormDef(d, _employeeSupportingPersonsPath(PrJmhzSupportingPersonEntity.PersonalIdentificationNumber), {
        isRequired: not(oneOfIsDefined((bc) => [
            bc.getParent().navigate(PrJmhzSupportingPersonEntity.DateOfBirth),
        ])),
        affectedFields: [
            { id: PrJmhzSupportingPersonEntity.DateOfBirth, navigateFromParent: true, revalidate: true }
        ],
        ...getJmhzIdDef(434)
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.SupportingPersons,
        ...employeeGroupDef,
        title: i18next.t("Submission:JmhzForm.SupportingPersons"),
        showGroupDivider: false,
        rows: [[
            { id: _employeeIncomeTaxOverviewPath(PrJmhzEmployeeIncomeTaxOverviewEntity.HasOtherSupportingPerson, false) }
        ]],
        lineItems: {
            collection: createPath(PrJmhzEmployeeEntity.IncomeTaxOverview, PrJmhzEmployeeIncomeTaxOverviewEntity.SupportingPersons),
            order: PrJmhzSupportingPersonEntity.Id,
            canAdd: ifTruthy(bc => bc.getParent().navigate(PrJmhzEmployeeIncomeTaxOverviewEntity.HasOtherSupportingPerson)),
            isTheOnlyItemRemovable: false,
            columns: [
                { id: PrJmhzSupportingPersonEntity.FirstName },
                { id: PrJmhzSupportingPersonEntity.LastName },
                { id: PrJmhzSupportingPersonEntity.DateOfBirth },
                { id: PrJmhzSupportingPersonEntity.PersonalIdentificationNumber },
            ]
        }
    });

    // *********************** Withholding Tax ***********************
    const _employeeWithHoldingTaxPath = (path: PrJmhzEmployeeWithholdingTaxOverviewEntity, pref = true) =>
        createPath(pref ? _employeePath(PrJmhzEmployeeEntity.WithholdingTaxOverview) : PrJmhzEmployeeEntity.WithholdingTaxOverview, path);

    addFormDef(d, _employeeWithHoldingTaxPath(PrJmhzEmployeeWithholdingTaxOverviewEntity.WithholdingTaxBase), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(307)
    });
    addFormDef(d, _employeeWithHoldingTaxPath(PrJmhzEmployeeWithholdingTaxOverviewEntity.BodyMemberReward), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(308)
    });
    addFormDef(d, _employeeWithHoldingTaxPath(PrJmhzEmployeeWithholdingTaxOverviewEntity.WithholdingTax), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(309)
    });
    addFormDef(d, _employeeWithHoldingTaxPath(PrJmhzEmployeeWithholdingTaxOverviewEntity.BodyMemberTaxAdvance), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(310)
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.WithholdingTaxOverview,
        ...employeeGroupDef,
        title: i18next.t("Submission:JmhzForm.WithholdingTaxOverview"),
        showGroupDivider: false,
        rows: [[
            { id: _employeeWithHoldingTaxPath(PrJmhzEmployeeWithholdingTaxOverviewEntity.WithholdingTaxBase, false) },
            { id: _employeeWithHoldingTaxPath(PrJmhzEmployeeWithholdingTaxOverviewEntity.BodyMemberReward, false) },
            { id: _employeeWithHoldingTaxPath(PrJmhzEmployeeWithholdingTaxOverviewEntity.WithholdingTax, false) },
            { id: _employeeWithHoldingTaxPath(PrJmhzEmployeeWithholdingTaxOverviewEntity.BodyMemberTaxAdvance, false) },
        ]]
    });

    // *********************** Annual Payment Overview ***********************
    const _employeeAnnualPaymentOverviewPath = (path: PrJmhzEmployeeAnnualPaymentOverviewEntity, pref = true) =>
        createPath(pref ? _employeePath(PrJmhzEmployeeEntity.AnnualPaymentOverview) : PrJmhzEmployeeEntity.AnnualPaymentOverview, path);

    addFormDef(d, _employeeAnnualPaymentOverviewPath(PrJmhzEmployeeAnnualPaymentOverviewEntity.TaxableIncome), {
        ...getPayrollCurrencyFieldDef(),
        isVisible: isInMonths([1]),
        ...getJmhzIdDef(313)
    });
    addFormDef(d, _employeeAnnualPaymentOverviewPath(PrJmhzEmployeeAnnualPaymentOverviewEntity.AdvancedTax), {
        ...getPayrollCurrencyFieldDef(),
        isVisible: isInMonths([1]),
        ...getJmhzIdDef(317)
    });
    addFormDef(d, _employeeAnnualPaymentOverviewPath(PrJmhzEmployeeAnnualPaymentOverviewEntity.AdditionalTaxableIncome), {
        ...getPayrollCurrencyFieldDef(),
        isVisible: isInMonths([1]),
        ...getJmhzIdDef(316)
    });
    addFormDef(d, _employeeAnnualPaymentOverviewPath(PrJmhzEmployeeAnnualPaymentOverviewEntity.AdditionalTaxAdvance), {
        ...getPayrollCurrencyFieldDef(),
        isVisible: isInMonths([1]),
        ...getJmhzIdDef(318)
    });
    addFormDef(d, _employeeAnnualPaymentOverviewPath(PrJmhzEmployeeAnnualPaymentOverviewEntity.WithholdingTaxIncome), {
        ...getPayrollCurrencyFieldDef(),
        isVisible: isInMonths([1]),
        ...getJmhzIdDef(311)
    });
    addFormDef(d, _employeeAnnualPaymentOverviewPath(PrJmhzEmployeeAnnualPaymentOverviewEntity.WithholdingTax), {
        ...getPayrollCurrencyFieldDef(),
        isVisible: isInMonths([1]),
        ...getJmhzIdDef(312)
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.AnnualPaymentOverview,
        ...employeeGroupDef,
        title: i18next.t("Submission:JmhzForm.AnnualPaymentOverview"),
        showGroupDivider: false,
        rows: [[
            { id: _employeeAnnualPaymentOverviewPath(PrJmhzEmployeeAnnualPaymentOverviewEntity.TaxableIncome, false) },
            { id: _employeeAnnualPaymentOverviewPath(PrJmhzEmployeeAnnualPaymentOverviewEntity.AdvancedTax, false) },
            { id: _employeeAnnualPaymentOverviewPath(PrJmhzEmployeeAnnualPaymentOverviewEntity.AdditionalTaxableIncome, false) },
            { id: _employeeAnnualPaymentOverviewPath(PrJmhzEmployeeAnnualPaymentOverviewEntity.AdditionalTaxAdvance, false) },
            { id: _employeeAnnualPaymentOverviewPath(PrJmhzEmployeeAnnualPaymentOverviewEntity.WithholdingTaxIncome, false) },
            { id: _employeeAnnualPaymentOverviewPath(PrJmhzEmployeeAnnualPaymentOverviewEntity.WithholdingTax, false) },
        ]]
    });

    // *********************** Annual Tax Settlement ***********************
    const _employeeAnnualTaxSettlementPath = (path: PrJmhzEmployeeAnnualTaxSettlementEntity, pref = true) =>
        createPath(pref ? _employeePath(PrJmhzEmployeeEntity.AnnualTaxSettlement) : PrJmhzEmployeeEntity.AnnualTaxSettlement, path);

    const _employeeAnnualTaxPartnersPath = (path: PrJmhzAnnualTaxSettlementPartnerEntity, pref = true) =>
        createPath(_employeeAnnualTaxSettlementPath(PrJmhzEmployeeAnnualTaxSettlementEntity.Partners, pref), path);


    addFormDef(d, _employeePath(PrJmhzEmployeeEntity.HasAnnualTaxSettlementRequest), {
        type: FieldType.ComboBox,
        isRequired: true,
        isVisible: isInMonths([1,2]),
        defaultValue: YesNoCode.No,
        fieldSettings: {
            displayName: YesNoEntity.Name
        },
        ...getJmhzIdDef(319)
    });
    addFormDef(d, _employeePath(PrJmhzEmployeeEntity.HasAnnualTaxSettlementPerformed), {
        type: FieldType.ComboBox,
        isRequired: true,
        isVisible: isInMonths([1,2,3]),
        defaultValue: YesNoCode.No,
        fieldSettings: {
            displayName: YesNoEntity.Name
        },
        affectedFields: [
            { id: _employeeAnnualTaxSettlementPath(PrJmhzEmployeeAnnualTaxSettlementEntity.Balance, false), navigateFromParent: true },
            { id: _employeeAnnualTaxSettlementPath(PrJmhzEmployeeAnnualTaxSettlementEntity.TaxBalance, false), navigateFromParent: true },
            { id: _employeeAnnualTaxSettlementPath(PrJmhzEmployeeAnnualTaxSettlementEntity.TaxBonusBalance, false), navigateFromParent: true }
        ],
        ...getJmhzIdDef(320)
    });
    addFormDef(d, _employeeAnnualTaxSettlementPath(PrJmhzEmployeeAnnualTaxSettlementEntity.Balance), {
        ...getPayrollCurrencyFieldDef(),
        isVisible: ifAll(isInMonths([1,2,3]), ifTruthy(bc => bc.getParent().getParent().navigate(PrJmhzEmployeeEntity.HasAnnualTaxSettlementPerformed))),
        ...getJmhzIdDef(321)
    });
    addFormDef(d, _employeeAnnualTaxSettlementPath(PrJmhzEmployeeAnnualTaxSettlementEntity.TaxBalance), {
        ...getPayrollCurrencyFieldDef(),
        isVisible: ifAll(isInMonths([1,2,3]), ifTruthy(bc => bc.getParent().getParent().navigate(PrJmhzEmployeeEntity.HasAnnualTaxSettlementPerformed))),
        ...getJmhzIdDef(322)
    });
    addFormDef(d, _employeeAnnualTaxSettlementPath(PrJmhzEmployeeAnnualTaxSettlementEntity.TaxBonusBalance), {
        ...getPayrollCurrencyFieldDef(),
        isVisible: ifAll(isInMonths([1,2,3]), ifTruthy(bc => bc.getParent().getParent().navigate(PrJmhzEmployeeEntity.HasAnnualTaxSettlementPerformed))),
        ...getJmhzIdDef(323)
    });
    addFormDef(d, _employeeAnnualTaxSettlementPath(PrJmhzEmployeeAnnualTaxSettlementEntity.HasSpouseDiscount), {
        ...getYesNoBooleanFieldDef(),
        isVisible: isInMonths([1,2,3]),
        affectedFields: [
            { id: PrJmhzEmployeeAnnualTaxSettlementEntity.HasSpouseDiscount, navigateFromParent: true /* refreshes containing group */ }
        ],
        ...getJmhzIdDef(420)
    });
    // Partners sub group
    addFormDef(d, _employeeAnnualTaxPartnersPath(PrJmhzAnnualTaxSettlementPartnerEntity.FirstName), {
        ...getJmhzIdDef(421)
    });
    addFormDef(d, _employeeAnnualTaxPartnersPath(PrJmhzAnnualTaxSettlementPartnerEntity.LastName), {
        ...getJmhzIdDef(422)
    });
    addFormDef(d, _employeeAnnualTaxPartnersPath(PrJmhzAnnualTaxSettlementPartnerEntity.PersonalIdentificationNumber), {
        isRequired: not(oneOfIsDefined((bc) => [
            bc.getParent().navigate(PrJmhzAnnualTaxSettlementPartnerEntity.DateOfBirth),
        ])),
        affectedFields: [
            { id: PrJmhzAnnualTaxSettlementPartnerEntity.DateOfBirth, navigateFromParent: true, revalidate: true }
        ],
        ...getJmhzIdDef(423)
    });
    addFormDef(d, _employeeAnnualTaxPartnersPath(PrJmhzAnnualTaxSettlementPartnerEntity.DateOfBirth), {
        isRequired: not(oneOfIsDefined((bc) => [
            bc.getParent().navigate(PrJmhzAnnualTaxSettlementPartnerEntity.PersonalIdentificationNumber),
        ])),
        affectedFields: [
            { id: PrJmhzAnnualTaxSettlementPartnerEntity.PersonalIdentificationNumber, navigateFromParent: true, revalidate: true }
        ],
        ...getJmhzIdDef(424)
    });
    // todo: Collection of children inside partners collection :-(
    //  ... 539, 540, 541, 542

    addFormDef(d, _employeeAnnualTaxPartnersPath(PrJmhzAnnualTaxSettlementPartnerEntity.IsZtpP), {
        ...getYesNoBooleanFieldDef(),
        ...getJmhzIdDef(425)
    });
    addFormDef(d, _employeeAnnualTaxPartnersPath(PrJmhzAnnualTaxSettlementPartnerEntity.DiscountMonthsCount), {
        fieldSettings: {
            unit: "měsíců"
        },
        ...getJmhzIdDef(426)
    });
    addFormDef(d, _employeeAnnualTaxPartnersPath(PrJmhzAnnualTaxSettlementPartnerEntity.ZtpPDiscountMonthsCount), {
        fieldSettings: {
            unit: "měsíců"
        },
        ...getJmhzIdDef(430)
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.AnnualTaxSettlement,
        ...employeeGroupDef,
        title: i18next.t("Submission:JmhzForm.AnnualTaxSettlement"),
        showGroupDivider: false,
        rows: [[
            { id: PrJmhzEmployeeEntity.HasAnnualTaxSettlementRequest },
            { id: PrJmhzEmployeeEntity.HasAnnualTaxSettlementPerformed },
            { id: _employeeAnnualTaxSettlementPath(PrJmhzEmployeeAnnualTaxSettlementEntity.Balance, false) },
            { id: _employeeAnnualTaxSettlementPath(PrJmhzEmployeeAnnualTaxSettlementEntity.TaxBalance, false) },
            { id: _employeeAnnualTaxSettlementPath(PrJmhzEmployeeAnnualTaxSettlementEntity.TaxBonusBalance, false) },
            { id: _employeeAnnualTaxSettlementPath(PrJmhzEmployeeAnnualTaxSettlementEntity.HasSpouseDiscount, false) },
        ]],
        lineItems: {
            collection: _employeeAnnualTaxSettlementPath(PrJmhzEmployeeAnnualTaxSettlementEntity.Partners, false),
            order: PrJmhzAnnualTaxSettlementPartnerEntity.Id,
            isTheOnlyItemRemovable: false,
            canAdd: ifAll(isInMonths([1,2,3]), ifTruthy(bc => bc.getParent().navigate(PrJmhzEmployeeAnnualTaxSettlementEntity.HasSpouseDiscount))),
            columns: [
                { id: PrJmhzAnnualTaxSettlementPartnerEntity.FirstName },
                { id: PrJmhzAnnualTaxSettlementPartnerEntity.LastName },
                { id: PrJmhzAnnualTaxSettlementPartnerEntity.DateOfBirth },
                { id: PrJmhzAnnualTaxSettlementPartnerEntity.PersonalIdentificationNumber },
                // todo: Inner collection of children inside partners collection :-(
                { id: PrJmhzAnnualTaxSettlementPartnerEntity.IsZtpP },
                { id: PrJmhzAnnualTaxSettlementPartnerEntity.DiscountMonthsCount },
                { id: PrJmhzAnnualTaxSettlementPartnerEntity.ZtpPDiscountMonthsCount },
            ]
        }
    });

    // Continue with SupportingPersons (another group but connected wo title)
    const _employeeAnnualTaxSupportingPersonsPath = (path: PrJmhzAnnualTaxSettlementSupportingPersonEntity, pref = true) =>
        createPath(_employeeAnnualTaxSettlementPath(PrJmhzEmployeeAnnualTaxSettlementEntity.SupportingPersons, pref), path);

    addFormDef(d, _employeeAnnualTaxSettlementPath(PrJmhzEmployeeAnnualTaxSettlementEntity.HasChildTaxBenefit), {
        ...getYesNoBooleanFieldDef(),
        isVisible: isInMonths([1,2,3]),
        affectedFields: [
            { id: PrJmhzEmployeeAnnualTaxSettlementEntity.HasOtherSupportingPerson, navigateFromParent: true }
        ],
        ...getJmhzIdDef(454)
    });
    addFormDef(d, _employeeAnnualTaxSettlementPath(PrJmhzEmployeeAnnualTaxSettlementEntity.HasOtherSupportingPerson), {
        ...getYesNoBooleanFieldDef(),
        isVisible: ifAll(isInMonths([1,2,3]), ifTruthy(bc => bc.getParent().navigate(PrJmhzEmployeeAnnualTaxSettlementEntity.HasChildTaxBenefit))),
        affectedFields: [
            { id: PrJmhzEmployeeAnnualTaxSettlementEntity.HasOtherSupportingPerson, navigateFromParent: true /* refresh containing group*/ }
        ],
        ...getJmhzIdDef(455)
    });
    addFormDef(d, _employeeAnnualTaxSupportingPersonsPath(PrJmhzAnnualTaxSettlementSupportingPersonEntity.FirstName), {
        ...getJmhzIdDef(441)
    });
    addFormDef(d, _employeeAnnualTaxSupportingPersonsPath(PrJmhzAnnualTaxSettlementSupportingPersonEntity.LastName), {
        ...getJmhzIdDef(442)
    });
    addFormDef(d, _employeeAnnualTaxSupportingPersonsPath(PrJmhzAnnualTaxSettlementSupportingPersonEntity.DateOfBirth), {
        isRequired: not(oneOfIsDefined((bc) => [
            bc.getParent().navigate(PrJmhzAnnualTaxSettlementSupportingPersonEntity.PersonalIdentificationNumber),
        ])),
        affectedFields: [
            { id: PrJmhzAnnualTaxSettlementSupportingPersonEntity.PersonalIdentificationNumber, navigateFromParent: true, revalidate: true }
        ],
        ...getJmhzIdDef(443)
    });
    addFormDef(d, _employeeAnnualTaxSupportingPersonsPath(PrJmhzAnnualTaxSettlementSupportingPersonEntity.PersonalIdentificationNumber), {
        isRequired: not(oneOfIsDefined((bc) => [
            bc.getParent().navigate(PrJmhzAnnualTaxSettlementSupportingPersonEntity.DateOfBirth),
        ])),
        affectedFields: [
            { id: PrJmhzAnnualTaxSettlementSupportingPersonEntity.DateOfBirth, navigateFromParent: true, revalidate: true }
        ],
        ...getJmhzIdDef(444)
    });
    addFormDef(d, _employeeAnnualTaxSupportingPersonsPath(PrJmhzAnnualTaxSettlementSupportingPersonEntity.DiscountMonths), {
        // todo: months multiselect definition...
        ...getJmhzIdDef(445)
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.AnnualTaxSettlement_2,
        ...employeeGroupDef,
        rows: [[
            { id: _employeeAnnualTaxSettlementPath(PrJmhzEmployeeAnnualTaxSettlementEntity.HasChildTaxBenefit, false) },
            { id: _employeeAnnualTaxSettlementPath(PrJmhzEmployeeAnnualTaxSettlementEntity.HasOtherSupportingPerson, false) },
        ]],
        lineItems: {
            collection: _employeeAnnualTaxSettlementPath(PrJmhzEmployeeAnnualTaxSettlementEntity.SupportingPersons, false),
            order: PrJmhzAnnualTaxSettlementSupportingPersonEntity.Id,
            isTheOnlyItemRemovable: false,
            canAdd: ifAll(isInMonths([1,2,3]), ifTruthy(bc => bc.getParent().navigate(PrJmhzEmployeeAnnualTaxSettlementEntity.HasOtherSupportingPerson))),
            columns: [
                { id: PrJmhzAnnualTaxSettlementSupportingPersonEntity.FirstName },
                { id: PrJmhzAnnualTaxSettlementSupportingPersonEntity.LastName },
                { id: PrJmhzAnnualTaxSettlementSupportingPersonEntity.DateOfBirth },
                { id: PrJmhzAnnualTaxSettlementSupportingPersonEntity.PersonalIdentificationNumber },
                { id: PrJmhzAnnualTaxSettlementSupportingPersonEntity.DiscountMonths },
            ]
        }
    });

    // Continue with SupportedChildren (another group but connected wo title)
    const _employeeAnnualTaxSupportedChildrenPath = (path: PrJmhzAnnualTaxSettlementSupportedChildEntity, pref = true) =>
        createPath(_employeeAnnualTaxSettlementPath(PrJmhzEmployeeAnnualTaxSettlementEntity.SupportedChildren, pref), path);

    addFormDef(d, _employeeAnnualTaxSupportedChildrenPath(PrJmhzAnnualTaxSettlementSupportedChildEntity.FirstName), {
        ...getJmhzIdDef(446)
    });
    addFormDef(d, _employeeAnnualTaxSupportedChildrenPath(PrJmhzAnnualTaxSettlementSupportedChildEntity.LastName), {
        ...getJmhzIdDef(447)
    });
    addFormDef(d, _employeeAnnualTaxSupportedChildrenPath(PrJmhzAnnualTaxSettlementSupportedChildEntity.DateOfBirth), {
        isRequired: not(oneOfIsDefined((bc) => [
            bc.getParent().navigate(PrJmhzAnnualTaxSettlementSupportedChildEntity.PersonalIdentificationNumber),
        ])),
        affectedFields: [
            { id: PrJmhzAnnualTaxSettlementSupportedChildEntity.PersonalIdentificationNumber, navigateFromParent: true, revalidate: true }
        ],
        ...getJmhzIdDef(448)
    });
    addFormDef(d, _employeeAnnualTaxSupportedChildrenPath(PrJmhzAnnualTaxSettlementSupportedChildEntity.PersonalIdentificationNumber), {
        isRequired: not(oneOfIsDefined((bc) => [
            bc.getParent().navigate(PrJmhzAnnualTaxSettlementSupportedChildEntity.DateOfBirth),
        ])),
        affectedFields: [
            { id: PrJmhzAnnualTaxSettlementSupportedChildEntity.DateOfBirth, navigateFromParent: true, revalidate: true }
        ],
        ...getJmhzIdDef(449)
    });
    addFormDef(d, _employeeAnnualTaxSupportedChildrenPath(PrJmhzAnnualTaxSettlementSupportedChildEntity.ZtpPDiscountMonths), {
        // todo: months multiselect definition...
        ...getJmhzIdDef(450)
    });
    addFormDef(d, _employeeAnnualTaxSupportedChildrenPath(PrJmhzAnnualTaxSettlementSupportedChildEntity.DiscountMonths), {
        // todo: months multiselect with 4 combined fields definition or whatever ???!!!???
        ...getJmhzIdDef(451)
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.AnnualTaxSettlement_3,
        ...employeeGroupDef,
        lineItems: {
            collection: _employeeAnnualTaxSettlementPath(PrJmhzEmployeeAnnualTaxSettlementEntity.SupportedChildren, false),
            order: PrJmhzAnnualTaxSettlementSupportedChildEntity.Id,
            isTheOnlyItemRemovable: false,
            canAdd: ifAll(isInMonths([1,2,3]), ifTruthy(bc => bc.getParent().navigate(PrJmhzEmployeeAnnualTaxSettlementEntity.HasChildTaxBenefit))),
            columns: [
                { id: PrJmhzAnnualTaxSettlementSupportedChildEntity.FirstName },
                { id: PrJmhzAnnualTaxSettlementSupportedChildEntity.LastName },
                { id: PrJmhzAnnualTaxSettlementSupportedChildEntity.DateOfBirth },
                { id: PrJmhzAnnualTaxSettlementSupportedChildEntity.PersonalIdentificationNumber },
                { id: PrJmhzAnnualTaxSettlementSupportedChildEntity.ZtpPDiscountMonths },
                { id: PrJmhzAnnualTaxSettlementSupportedChildEntity.DiscountMonths },
            ]
        }
    });

    /**
     * *********************** Employee->Employments groups ***********************
     */

    const employmentGroupDef: Partial<IFormGroupDef> = {
        collection: _employmentPath(),
        parentCollections: [_employeePath()],
        collectionOrder: PrJmhzEmploymentEntity.Id,
    };

    /**
     * *********************** Employee->Employments->Metadata ***********************
     */
    addFormDef(d, _employmentPath(PrJmhzEmploymentEntity.FormGuid), {
        ...getJmhzIdDef(12)
    });
    addFormDef(d, _employmentPath(PrJmhzEmploymentEntity.FormType), {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: PrJmhzFormTypeEntity.MpsvCode,
            shouldDisplayAdditionalColumns: true,
            orderBy: {
                propertyName: PrJmhzFormTypeEntity.MpsvCode,
                asc: true
            },
            preloadItems: true
        },
        columns: [
            { id: PrJmhzFormTypeEntity.MpsvCode },
            { id: PrJmhzFormTypeEntity.Name },
        ],
        ...getJmhzIdDef(16)
    });
    addFormDef(d, _employmentPath(PrJmhzEmploymentEntity.IsDelayedIncome), {
        ...getYesNoBooleanFieldDef(),
        ...getJmhzIdDef(548)
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.EmploymentMetadata,
        ...employmentGroupDef,
        title: i18next.t("Submission:JmhzForm.Metadata"),
        collectionItemTitle: (index: number, item: IEntity) => {
            return item.Employment?.Name
                || i18next.t("Submission:JmhzForm.NthEmployment", { idx: index + 1 });
        },
        showGroupDivider: false,
        rows: [[
            { id: _employmentPath(PrJmhzEmploymentEntity.FormGuid, false) },
            { id: _employmentPath(PrJmhzEmploymentEntity.FormType, false) },
            // todo: ??? 495
            { id: _employmentPath(PrJmhzEmploymentEntity.IsDelayedIncome, false) },
            // todo: ??? 537, 538
        ]]
    });

    /**
     * *********************** Employee->Employments->Workplace ***********************
     */
    addFormDef(d, _employmentPath(PrJmhzEmploymentEntity.DateStart), {
        ...getJmhzIdDef(223)
    });
    addFormDef(d, _employmentPath(PrJmhzEmploymentEntity.MpsvIdentifier), {
        ...getJmhzIdDef(228)
    });
    addFormDef(d, _employmentPath(PrJmhzEmploymentEntity.City), {
        ...getAddressAutocompleteDef("City"),
        ...getJmhzIdDef(229)
    });
    addFormDef(d, _employmentPath(PrJmhzEmploymentEntity.CityCode), {
        ...getAddressAutocompleteDef("AddressCode"),
        ...getJmhzIdDef(230)
    });
    addFormDef(d, _employmentPath(PrJmhzEmploymentEntity.Country), {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: CountryEntity.Name
        },
        ...getJmhzIdDef(231)
    });
    addFormDef(d, _employmentPath(PrJmhzEmploymentEntity.CsszEmploymentType), {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: CsszEmploymentTypeEntity.Name
        },
        ...getJmhzIdDef(239)
    });
    addFormDef(d, _employmentPath(PrJmhzEmploymentEntity.HasFunctionalBenefits), {
        ...getYesNoBooleanFieldDef(),
        ...getJmhzIdDef(247)
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.WorkingPlace,
        ...employmentGroupDef,
        title: i18next.t("Submission:JmhzForm.WorkingPlace"),
        showGroupDivider: false,
        rows: [[
            { id: _employmentPath(PrJmhzEmploymentEntity.DateStart, false) },
            { id: _employmentPath(PrJmhzEmploymentEntity.MpsvIdentifier, false) },
            { id: _employmentPath(PrJmhzEmploymentEntity.City, false) },
            { id: _employmentPath(PrJmhzEmploymentEntity.CityCode, false) },
            { id: _employmentPath(PrJmhzEmploymentEntity.Country, false) },
            { id: _employmentPath(PrJmhzEmploymentEntity.CsszEmploymentType, false) },
            { id: _employmentPath(PrJmhzEmploymentEntity.HasFunctionalBenefits, false) },
        ]]
    });

    /**
     * *********************** Employee->Employments->TemporaryAssignment ***********************
     */
    addFormDef(d, _employmentPath(PrJmhzEmploymentEntity.IsTemporaryAssignmentToUser), {
        ...getYesNoBooleanFieldDef(),
        defaultValue: YesNoCode.No,
        isDisabled: true,
        ...getJmhzIdDef(251)
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.TemporaryAssignment,
        ...employmentGroupDef,
        title: i18next.t("Submission:JmhzForm.TemporaryAssignment"),
        showGroupDivider: false,
        rows: [[
            { id: _employmentPath(PrJmhzEmploymentEntity.IsTemporaryAssignmentToUser, false) },
            // todo: additional disabled fields ???
        ]]
    });

    /**
     * *********************** Employee->Employments->EmploymentPolicy ***********************
     */
    addFormDef(d, _employmentPath(PrJmhzEmploymentEntity.HasActiveEmploymentPolicyToolContribution), {
        ...getYesNoBooleanFieldDef(),
        defaultValue: YesNoCode.No,
        isDisabled: true,
        ...getJmhzIdDef(232)
    });
    // todo: ???
    // addFormDef(d, _employmentPath(PrJmhzEmploymentEntity.ActiveEmploymentPolicyTool), {
    //     type: FieldType.ComboBox,
    //     fieldSettings: {
    //         displayName: ActiveEmploymentPolicyToolEntity.Name
    //     },
    //     ...getJmhzIdDef(233)
    // });

    addFormGroup(d, {
        id: PrSubmissionGroup.EmploymentPolicy,
        ...employmentGroupDef,
        title: i18next.t("Submission:JmhzForm.EmploymentPolicy"),
        showGroupDivider: false,
        rows: [[
            { id: _employmentPath(PrJmhzEmploymentEntity.HasActiveEmploymentPolicyToolContribution, false) },
            // { id: _employmentPath(PrJmhzEmploymentEntity.ActiveEmploymentPolicyTool, false) },
        ]]
    });

    /**
     * *********************** Employee->Employments->WorkingPattern ***********************
     */
    addFormDef(d, _employmentPath(PrJmhzEmploymentEntity.MonthlyWorkingHoursFullTime), {
        fieldSettings: {
            unit: "hodin"
        },
        ...getJmhzIdDef(259)
    });
    addFormDef(d, _employmentPath(PrJmhzEmploymentEntity.MonthlyWorkingHours), {
        fieldSettings: {
            unit: "hodin"
        },
        ...getJmhzIdDef(260)
    });
    addFormDef(d, _employmentPath(PrJmhzEmploymentEntity.HoursPerWeekFullTime), {
        fieldSettings: {
            unit: "hodin"
        },
        ...getJmhzIdDef(261)
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.WorkingPattern,
        ...employmentGroupDef,
        title: i18next.t("Submission:JmhzForm.WorkingPattern"),
        showGroupDivider: false,
        rows: [[
            { id: _employmentPath(PrJmhzEmploymentEntity.MonthlyWorkingHoursFullTime, false) },
            { id: _employmentPath(PrJmhzEmploymentEntity.MonthlyWorkingHours, false) },
            { id: _employmentPath(PrJmhzEmploymentEntity.HoursPerWeekFullTime, false) },
        ]]
    });

    /**
     * *********************** Employee->Employments->AttendanceOverview ***********************
     */
    const _employmentAttendanceOverviewPath = (path: PrJmhzEmploymentAttendanceOverviewEntity, pref = true) =>
        createPath(_employmentPath(PrJmhzEmploymentEntity.AttendanceOverview, pref), path);

    addFormDef(d, _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.DaysInRegistrationState), {
        fieldSettings: {
            unit: "dnů"
        },
        ...getJmhzIdDef(265)
    });
    addFormDef(d, _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.WorkedDays), {
        fieldSettings: {
            unit: "dnů"
        },
        ...getJmhzIdDef(267)
    });
    addFormDef(d, _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.WorkedHours), {
        fieldSettings: {
            unit: "hodin"
        },
        ...getJmhzIdDef(268)
    });
    addFormDef(d, _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.OvertimeHours), {
        fieldSettings: {
            unit: "hodin"
        },
        ...getJmhzIdDef(269)
    });
    addFormDef(d, _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.RiskWorkHours), {
        fieldSettings: {
            unit: "hodin"
        },
        affectedFields: [
            { id: PrJmhzEmploymentAttendanceOverviewEntity.RiskCategory, navigateFromParent: true }
        ],
        ...getJmhzIdDef(273)
    });
    addFormDef(d, _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.RiskCategory), {
        type: FieldType.ComboBox,
        isRequired: true,
        isVisible: isNonZeroValue(bc => bc.getParent().navigate(PrJmhzEmploymentAttendanceOverviewEntity.RiskWorkHours)),
        fieldSettings: {
            displayName: RiskCategoryEntity.Name
        },
        ...getJmhzIdDef(274)
    });

    addFormDef(d, _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.NonWorkedHoursTotal), {
        fieldSettings: {
            unit: "hodin"
        },
        ...getJmhzIdDef(275)
    });
    addFormDef(d, _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.NonWorkedHoursWithCompensation), {
        fieldSettings: {
            unit: "hodin"
        },
        ...getJmhzIdDef(276)
    });
    addFormDef(d, _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.NonWorkedHoursTemporaryIncapacityWithCompensation), {
        fieldSettings: {
            unit: "hodin"
        },
        ...getJmhzIdDef(278)
    });
    addFormDef(d, _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.NonWorkedHoursTemporaryIncapacityWithoutCompensation), {
        fieldSettings: {
            unit: "hodin"
        },
        ...getJmhzIdDef(277)
    });
    addFormDef(d, _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.NonWorkedHoursVacation), {
        fieldSettings: {
            unit: "hodin"
        },
        ...getJmhzIdDef(279)
    });
    addFormDef(d, _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.NonWorkedHoursCareAllowance), {
        fieldSettings: {
            unit: "hodin"
        },
        ...getJmhzIdDef(280)
    });
    addFormDef(d, _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.EmployeeObstacleNonWorkedHours), {
        fieldSettings: {
            unit: "hodin"
        },
        ...getJmhzIdDef(471)
    });
    addFormDef(d, _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.EmployerObstacleNonWorkedHours), {
        fieldSettings: {
            unit: "hodin"
        },
        ...getJmhzIdDef(472)
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.AttendanceOverview,
        ...employmentGroupDef,
        title: i18next.t("Submission:JmhzForm.AttendanceOverview"),
        showGroupDivider: false,
        rows: [[
            { id: _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.DaysInRegistrationState, false) },
            { id: _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.WorkedDays, false) },
            { id: _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.WorkedHours, false) },
            { id: _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.OvertimeHours, false) },
            // todo: 270, 271, 272
            { id: _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.RiskWorkHours, false) },
            { id: _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.RiskCategory, false) },

            { id: _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.NonWorkedHoursTotal, false) },
            { id: _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.NonWorkedHoursWithCompensation, false) },
            { id: _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.NonWorkedHoursTemporaryIncapacityWithCompensation, false) },
            { id: _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.NonWorkedHoursTemporaryIncapacityWithoutCompensation, false) },
            { id: _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.NonWorkedHoursVacation, false) },
            { id: _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.NonWorkedHoursCareAllowance, false) },
            { id: _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.EmployeeObstacleNonWorkedHours, false) },
            { id: _employmentAttendanceOverviewPath(PrJmhzEmploymentAttendanceOverviewEntity.EmployerObstacleNonWorkedHours, false) },
        ]]
    });

    /**
     * *********************** Employee->Employments->Preparations ***********************
     */
    // todo: Fields 263, 264 ???

    /**
     * *********************** Employee->Employments->IncomeOverview ***********************
     */
    addFormDef(d, _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.TaxCalculationBase), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(535)
    });
    addFormDef(d, _employmentPath(PrJmhzEmploymentEntity.DateSpecificLegalFactPayDay), {
        ...getJmhzIdDef(410)
    });
    addFormDef(d, _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.SettledWage), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(328)
    });
    addFormDef(d, _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.TariffWages), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(329)
    });
    addFormDef(d, _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.RegularBonusesAndRewards), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(330)
    });
    addFormDef(d, _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.IrregularBonusesAndRewards), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(331)
    });
    addFormDef(d, _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.SupplementsTotal), {
        ...getPayrollCurrencyFieldDef(),
        isDisabled: true,
        ...getJmhzIdDef(332)
    });
    addFormDef(d, _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.OvertimeSupplement), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(333)
    });
    addFormDef(d, _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.NightWorkSupplement), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(334)
    });
    addFormDef(d, _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.WeekendWorkSupplement), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(335)
    });
    addFormDef(d, _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.HolidayWorkSupplement), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(336)
    });
    addFormDef(d, _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.WageCompensationSettled), {
        ...getPayrollCurrencyFieldDef(),
        isDisabled: true,
        ...getJmhzIdDef(337)
    });
    addFormDef(d, _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.VacationCompensation), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(338)
    });
    addFormDef(d, _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.HolidayCompensation), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(339)
    });
    addFormDef(d, _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.EmployerObstacleCompensation), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(340)
    });
    addFormDef(d, _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.EmployeeObstacleCompensation), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(341)
    });
    addFormDef(d, _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.TemporaryIncapacityCompensation), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(342)
    });
    addFormDef(d, _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.StandbyDutyReward), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(343)
    });
    addFormDef(d, _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.AverageGrossHourlyEarnings), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(345)
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.IncomeOverview,
        ...employmentGroupDef,
        title: i18next.t("Submission:JmhzForm.IncomeOverview"),
        showGroupDivider: false,
        rows: [[
            { id: _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.TaxCalculationBase, false) },
            { id: _employmentPath(PrJmhzEmploymentEntity.DateSpecificLegalFactPayDay, false) },
            { id: _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.SettledWage, false) },
            { id: _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.TariffWages, false) },
            { id: _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.RegularBonusesAndRewards, false) },
            { id: _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.IrregularBonusesAndRewards, false) },
            { id: _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.SupplementsTotal, false) },
            { id: _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.OvertimeSupplement, false) },
            { id: _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.NightWorkSupplement, false) },
            { id: _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.WeekendWorkSupplement, false) },
            { id: _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.HolidayWorkSupplement, false) },
            { id: _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.WageCompensationSettled, false) },
            { id: _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.VacationCompensation, false) },
            { id: _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.HolidayCompensation, false) },
            { id: _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.EmployerObstacleCompensation, false) },
            { id: _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.EmployeeObstacleCompensation, false) },
            { id: _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.TemporaryIncapacityCompensation, false) },
            { id: _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.StandbyDutyReward, false) },
            { id: _employmentIncomeOverviewPath(PrJmhzEmploymentIncomeOverviewEntity.AverageGrossHourlyEarnings, false) },
        ]]
    });


    /**
     * *********************** Employee->Employments->ELDP ***********************
     */
    const _employmentSocialSecurityPath = (path: PrJmhzEmploymentSocialSecurityOverviewEntity, pref = true) =>
        createPath(_employmentPath(PrJmhzEmploymentEntity.SocialSecurityOverview, pref), path);
    const _employmentEldpPath = (path: PrJmhzEldpItemEntity | string, pref = true) =>
        createPath(_employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.EldpItems, pref), path);

    addFormDef(d, _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.DateInsuranceFrom), {
        ...getJmhzIdDef(354)
    });
    addFormDef(d, _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.DateInsuranceTo), {
        ...getJmhzIdDef(355)
    });
    addFormDef(d, _employmentEldpPath(PrJmhzEldpItemEntity.PensionInsuranceDays), {
        fieldSettings: {
            unit: "dnů"
        },
        ...getJmhzIdDef(356)
    });
    addFormDef(d, _employmentEldpPath(PrJmhzEldpItemEntity.SocialSecurityCalculationBase), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(245)
    });
    addFormDef(d, _employmentEldpPath(PrJmhzEldpItemEntity.ItemType), {
        type: FieldType.ComboBox,
        fieldSettings: {
            displayName: PrEldpItemTypeEntity.Name
        },
        ...getJmhzIdDef(240)
    });
    addFormDef(d, _employmentEldpPath(PrJmhzEldpItemEntity.DateValidFrom), {
        ...getJmhzIdDef(241)
    });
    addFormDef(d, _employmentEldpPath(PrJmhzEldpItemEntity.DateValidTo), {
        ...getJmhzIdDef(242)
    });
    addFormDef(d, _employmentEldpPath(createPath(PrJmhzEldpItemEntity.DaysExcluded, PrJmhzEldpDaysExcludedEntity.ExcludedPeriodsTotal)), {
        fieldSettings: {
            unit: "dnů"
        },
        ...getJmhzIdDef(357)
    });
    addFormDef(d, _employmentEldpPath(createPath(PrJmhzEldpItemEntity.DaysExcluded, PrJmhzEldpDaysExcludedEntity.TemporaryIncapacityDays)), {
        fieldSettings: {
            unit: "dnů"
        },
        ...getJmhzIdDef(358)
    });
    addFormDef(d, _employmentEldpPath(createPath(PrJmhzEldpItemEntity.DaysExcluded, PrJmhzEldpDaysExcludedEntity.MaternityAidDays)), {
        fieldSettings: {
            unit: "dnů"
        },
        ...getJmhzIdDef(359)
    });
    addFormDef(d, _employmentEldpPath(createPath(PrJmhzEldpItemEntity.DaysExcluded, PrJmhzEldpDaysExcludedEntity.CareAllowanceDays)), {
        fieldSettings: {
            unit: "dnů"
        },
        ...getJmhzIdDef(360)
    });
    addFormDef(d, _employmentEldpPath(createPath(PrJmhzEldpItemEntity.DaysExcluded, PrJmhzEldpDaysExcludedEntity.PaternityLeaveDays)), {
        fieldSettings: {
            unit: "dnů"
        },
        ...getJmhzIdDef(362)
    });
    addFormDef(d, _employmentEldpPath(createPath(PrJmhzEldpItemEntity.DaysExcluded, PrJmhzEldpDaysExcludedEntity.Paragraph16Days)), {
        fieldSettings: {
            unit: "dnů"
        },
        ...getJmhzIdDef(536)
    });
    addFormDef(d, _employmentEldpPath(createPath(PrJmhzEldpItemEntity.DaysExcluded, PrJmhzEldpDaysExcludedEntity.Paragraph18Days)), {
        fieldSettings: {
            unit: "dnů"
        },
        ...getJmhzIdDef(366)
    });
    addFormDef(d, _employmentEldpPath(createPath(PrJmhzEldpItemEntity.DaysExcluded, PrJmhzEldpDaysExcludedEntity.ExcusedAbsenceDays)), {
        fieldSettings: {
            unit: "dnů"
        },
        ...getJmhzIdDef(473)
    });
    addFormDef(d, _employmentEldpPath(createPath(PrJmhzEldpItemEntity.DaysExcluded, PrJmhzEldpDaysExcludedEntity.CompensatedTemporaryIncapacityDays)), {
        fieldSettings: {
            unit: "dnů"
        },
        ...getJmhzIdDef(474)
    });
    addFormDef(d, _employmentEldpPath(createPath(PrJmhzEldpItemEntity.DaysExcluded, PrJmhzEldpDaysExcludedEntity.PaidBenefitsDays)), {
        fieldSettings: {
            unit: "dnů"
        },
        ...getJmhzIdDef(475)
    });

    addFormDef(d, _employmentEldpPath(createPath(PrJmhzEldpItemEntity.DaysDeducted, PrJmhzEldpDaysDeductedEntity.DeductedPeriodsTotal)), {
        fieldSettings: {
            unit: "dnů"
        },
        ...getJmhzIdDef(375)
    });
    addFormDef(d, _employmentEldpPath(createPath(PrJmhzEldpItemEntity.DaysDeducted, PrJmhzEldpDaysDeductedEntity.TemporaryIncapacityDays)), {
        fieldSettings: {
            unit: "dnů"
        },
        ...getJmhzIdDef(462)
    });
    addFormDef(d, _employmentEldpPath(createPath(PrJmhzEldpItemEntity.DaysDeducted, PrJmhzEldpDaysDeductedEntity.MaternityAidDays)), {
        fieldSettings: {
            unit: "dnů"
        },
        ...getJmhzIdDef(463)
    });
    addFormDef(d, _employmentEldpPath(createPath(PrJmhzEldpItemEntity.DaysDeducted, PrJmhzEldpDaysDeductedEntity.CareAllowanceDays)), {
        fieldSettings: {
            unit: "dnů"
        },
        ...getJmhzIdDef(464)
    });
    addFormDef(d, _employmentEldpPath(createPath(PrJmhzEldpItemEntity.DaysDeducted, PrJmhzEldpDaysDeductedEntity.CareWithoutAllowanceDays)), {
        fieldSettings: {
            unit: "dnů"
        },
        ...getJmhzIdDef(465)
    });
    addFormDef(d, _employmentEldpPath(createPath(PrJmhzEldpItemEntity.DaysDeducted, PrJmhzEldpDaysDeductedEntity.PaternityLeaveDays)), {
        fieldSettings: {
            unit: "dnů"
        },
        ...getJmhzIdDef(466)
    });
    addFormDef(d, _employmentEldpPath(createPath(PrJmhzEldpItemEntity.DaysDeducted, PrJmhzEldpDaysDeductedEntity.UnpaidLeaveDays)), {
        fieldSettings: {
            unit: "dnů"
        },
        ...getJmhzIdDef(468)
    });
    addFormDef(d, _employmentEldpPath(createPath(PrJmhzEldpItemEntity.DaysDeducted, PrJmhzEldpDaysDeductedEntity.UnexcusedAbsenceDays)), {
        fieldSettings: {
            unit: "dnů"
        },
        ...getJmhzIdDef(469)
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.ELDP,
        ...employmentGroupDef,
        title: i18next.t("Submission:JmhzForm.ELDP"),
        showGroupDivider: false,
        rows: [[
            { id: _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.DateInsuranceFrom, false) },
            { id: _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.DateInsuranceTo, false) },
        ]],
        lineItems: {
            collection: createPath(PrJmhzEmploymentEntity.SocialSecurityOverview, PrJmhzEmploymentSocialSecurityOverviewEntity.EldpItems),
            columns: [
                { id: PrJmhzEldpItemEntity.PensionInsuranceDays },
                { id: PrJmhzEldpItemEntity.SocialSecurityCalculationBase },
                { id: PrJmhzEldpItemEntity.ItemType },
                { id: PrJmhzEldpItemEntity.DateValidFrom },
                { id: PrJmhzEldpItemEntity.DateValidTo },
                { id: createPath(PrJmhzEldpItemEntity.DaysExcluded, PrJmhzEldpDaysExcludedEntity.ExcludedPeriodsTotal) },
                { id: createPath(PrJmhzEldpItemEntity.DaysExcluded, PrJmhzEldpDaysExcludedEntity.TemporaryIncapacityDays) },
                { id: createPath(PrJmhzEldpItemEntity.DaysExcluded, PrJmhzEldpDaysExcludedEntity.MaternityAidDays) },
                { id: createPath(PrJmhzEldpItemEntity.DaysExcluded, PrJmhzEldpDaysExcludedEntity.CareAllowanceDays) },
                { id: createPath(PrJmhzEldpItemEntity.DaysExcluded, PrJmhzEldpDaysExcludedEntity.PaternityLeaveDays) },
                { id: createPath(PrJmhzEldpItemEntity.DaysExcluded, PrJmhzEldpDaysExcludedEntity.Paragraph16Days) },
                { id: createPath(PrJmhzEldpItemEntity.DaysExcluded, PrJmhzEldpDaysExcludedEntity.Paragraph18Days) },
                { id: createPath(PrJmhzEldpItemEntity.DaysExcluded, PrJmhzEldpDaysExcludedEntity.ExcusedAbsenceDays) },
                { id: createPath(PrJmhzEldpItemEntity.DaysExcluded, PrJmhzEldpDaysExcludedEntity.CompensatedTemporaryIncapacityDays) },
                { id: createPath(PrJmhzEldpItemEntity.DaysExcluded, PrJmhzEldpDaysExcludedEntity.PaidBenefitsDays) },
                { id: createPath(PrJmhzEldpItemEntity.DaysDeducted, PrJmhzEldpDaysDeductedEntity.DeductedPeriodsTotal) },
                { id: createPath(PrJmhzEldpItemEntity.DaysDeducted, PrJmhzEldpDaysDeductedEntity.TemporaryIncapacityDays) },
                { id: createPath(PrJmhzEldpItemEntity.DaysDeducted, PrJmhzEldpDaysDeductedEntity.MaternityAidDays) },
                { id: createPath(PrJmhzEldpItemEntity.DaysDeducted, PrJmhzEldpDaysDeductedEntity.CareAllowanceDays) },
                { id: createPath(PrJmhzEldpItemEntity.DaysDeducted, PrJmhzEldpDaysDeductedEntity.CareWithoutAllowanceDays) },
                { id: createPath(PrJmhzEldpItemEntity.DaysDeducted, PrJmhzEldpDaysDeductedEntity.PaternityLeaveDays) },
                { id: createPath(PrJmhzEldpItemEntity.DaysDeducted, PrJmhzEldpDaysDeductedEntity.UnpaidLeaveDays) },
                { id: createPath(PrJmhzEldpItemEntity.DaysDeducted, PrJmhzEldpDaysDeductedEntity.UnexcusedAbsenceDays) },
            ],
            order: PrJmhzEldpItemEntity.Id
        }
    });

    /**
     * *********************** Employee->Employments->CalculationBase ***********************
     */
    addFormDef(d, _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.IncomeTotal), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(476)
    });
    addFormDef(d, _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.EmployeeCalculationBaseInsurance), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(477)
    });
    addFormDef(d, _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.EmployerCalculationBaseSection5aA), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(478)
    });
    addFormDef(d, _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.EmployerCalculationBaseSection5aB), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(479)
    });
    addFormDef(d, _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.EmployerCalculationBaseSection5aC), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(480)
    });

    addFormGroup(d, {
        id: PrSubmissionGroup.CalculationBase,
        ...employmentGroupDef,
        title: i18next.t("Submission:JmhzForm.CalculationBase"),
        showGroupDivider: false,
        rows: [[
            { id: _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.IncomeTotal, false) },
            { id: _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.EmployeeCalculationBaseInsurance, false) },
            { id: _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.EmployerCalculationBaseSection5aA, false) },
            { id: _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.EmployerCalculationBaseSection5aB, false) },
            { id: _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.EmployerCalculationBaseSection5aC, false) },
        ]],
    });

    /**
     * *********************** Employee->Employments->InsuranceAndBonuses ***********************
     */
    addFormDef(d, _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.EmployerSocialSecurityInsurance), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(481)
    });
    addFormDef(d, _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.EmployeeSocialSecurityInsurance), {
        ...getPayrollCurrencyFieldDef(),
        ...getJmhzIdDef(370)
    });
    addFormDef(d, _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.HasEmployerInsuranceDiscount), {
        ...getYesNoBooleanFieldDef(),
        ...getJmhzIdDef(372),
        affectedFields: [
            {
                id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.EmployerDiscountEmployeeCount),
                revalidate: true
            },
            {
                id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.EmployerDiscountCalculationBase),
                revalidate: true
            },
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.EmployerDiscountAmount), revalidate: true },
            { id: PrJmhzEmploymentSocialSecurityOverviewEntity.ShorterWorkingTimeScope, navigateFromParent: true },
            { id: PrJmhzEmploymentSocialSecurityOverviewEntity.EmployerInsuranceDiscountType, navigateFromParent: true },
        ]
    });
    addFormDef(d, _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.ShorterWorkingTimeScope), {
        ...getPayrollCurrencyFieldDef(),
        isRequired: true,
        isVisible: ifTruthy(bc => bc.getParent().navigate(PrJmhzEmploymentSocialSecurityOverviewEntity.HasEmployerInsuranceDiscount)),
        ...getJmhzIdDef(373)
    });
    addFormDef(d, _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.EmployerInsuranceDiscountType), {
        type: FieldType.ComboBox,
        isRequired: true,
        isVisible: ifTruthy(bc => bc.getParent().navigate(PrJmhzEmploymentSocialSecurityOverviewEntity.HasEmployerInsuranceDiscount)),
        fieldSettings: {
            displayName: PrSocialInsuranceDiscountTypeEntity.Name
        },
        ...getJmhzIdDef(374)
    });

    addFormDef(d, _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.HasRetireeDiscount), {
        ...getYesNoBooleanFieldDef(),
        ...getJmhzIdDef(490),
        affectedFields: [
            {
                id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.EmployeeDiscountEmployeeCount),
                revalidate: true
            },
            {
                id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.EmployeeDiscountCalculationBase),
                revalidate: true
            },
            { id: _socialInsurancePath(PrJmhzSocialInsuranceOverviewEntity.EmployeeDiscountAmount), revalidate: true },
            { id: PrJmhzEmploymentSocialSecurityOverviewEntity.RetireeDiscountAmount, navigateFromParent: true },
        ]
    });
    addFormDef(d, _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.RetireeDiscountAmount), {
        ...getPayrollCurrencyFieldDef(),
        isRequired: true,
        isVisible: ifTruthy(bc => bc.getParent().navigate(PrJmhzEmploymentSocialSecurityOverviewEntity.HasRetireeDiscount)),
        ...getJmhzIdDef(491)
    });
    // todo: 546, 547
    // addFormDef(d, _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.), {
    //     ...getYesNoBooleanFieldDef(),
    //     ...getJmhzIdDef(546)
    // });
    // addFormDef(d, _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.), {
    //     ...getPayrollCurrencyFieldDef(),
    //     ...getJmhzIdDef(547)
    // });

    addFormGroup(d, {
        id: PrSubmissionGroup.InsuranceAndBonuses,
        ...employmentGroupDef,
        title: i18next.t("Submission:JmhzForm.InsuranceAndBonuses"),
        showGroupDivider: false,
        rows: [[
            { id: _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.EmployerSocialSecurityInsurance, false) },
            { id: _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.EmployeeSocialSecurityInsurance, false) },
            { id: _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.HasEmployerInsuranceDiscount, false) },
            { id: _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.ShorterWorkingTimeScope, false) },
            { id: _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.EmployerInsuranceDiscountType, false) },
            { id: _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.HasRetireeDiscount, false) },
            { id: _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity.RetireeDiscountAmount, false) },
            // todo: 546, 547
            // { id: _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity., false) },
            // { id: _employmentSocialSecurityPath(PrJmhzEmploymentSocialSecurityOverviewEntity., false) },
        ]],
    });

    return {
        ...d,
        entitySet: EntitySetName.PrJmhzSubmissions
    };
};

getDefinitions.translationFiles = [
    ...commonDef.translationFiles,
];
setDefByEntityType(EntityTypeName.PrJmhzSubmission, getDefinitions);